import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Clock } from 'lucide-react';

// The board's premium time picker: looks like a quiet field, opens an
// in-theme popover with hour / minute / AM-PM columns (native
// <input type="time"> is unstylable and ignores dark mode). Nothing is
// hardcoded to a window — any time, 5-minute steps.
// `value` is 'HH:MM' (24h); onChange receives the same shape.

const HOURS = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);

const parse = (value) => {
  const [h, m] = String(value || '08:00')
    .slice(0, 5)
    .split(':')
    .map(Number);
  return { h24: Number.isNaN(h) ? 8 : h, m: Number.isNaN(m) ? 0 : m };
};

const compose = (h12, m, pm) => {
  const h24 = (h12 % 12) + (pm ? 12 : 0);
  return `${String(h24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

export const formatTime = (value) => {
  const { h24, m } = parse(value);
  const pm = h24 >= 12;
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return m
    ? `${h12}:${String(m).padStart(2, '0')} ${pm ? 'PM' : 'AM'}`
    : `${h12} ${pm ? 'PM' : 'AM'}`;
};

const Column = ({ items, selected, onPick, render = (v) => v }) => {
  const ref = useRef(null);
  // Center the selected row when the popover opens.
  useEffect(() => {
    const el = ref.current?.querySelector('[data-sel="true"]');
    if (el && ref.current) {
      ref.current.scrollTop =
        el.offsetTop - ref.current.clientHeight / 2 + el.clientHeight / 2;
    }
  }, []);
  return (
    <div ref={ref} className="os-scroll max-h-44 w-12 overflow-y-auto py-1">
      {items.map((v) => (
        <button
          key={String(v)}
          type="button"
          data-sel={v === selected ? 'true' : undefined}
          onClick={() => onPick(v)}
          className={`mx-auto my-0.5 flex h-7 w-9 items-center justify-center rounded-md text-xs font-semibold transition-colors ${
            v === selected
              ? 'bg-slate-900 text-white dark:bg-slate-200 dark:!text-slate-900'
              : 'text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-white/10'
          }`}
        >
          {render(v)}
        </button>
      ))}
    </div>
  );
};

/**
 * minTime / maxTime ('HH:MM', optional) bound the pickable range (Nic
 * 2026-08-15). Used by the schedule editor so a specialist's shift can only
 * ever NARROW inside the org working hours: half days are the point, extending
 * past closing is not, and the engine measures every step against the ORG day.
 *
 * Clamped on the way OUT rather than by greying rows: the hour column uses a
 * nearest-meridiem heuristic (picking "8" while on 12 PM yields 08:00), so the
 * only reliable place to bound the result is after compose().
 */
const TimeField = ({
  value,
  onChange,
  disabled = false,
  title,
  minTime = null,
  maxTime = null,
}) => {
  const [open, setOpen] = useState(false);
  // Popover placement (2026-09-01): rendered through a PORTAL with FIXED
  // coordinates off the button's rectangle. An absolutely-positioned child
  // got clipped by any scrolling ancestor (the schedule rail) and could
  // spill past its edge; fixed + portal floats above everything, flips up
  // when the viewport bottom is near, and clamps inside the viewport.
  const [anchor, setAnchor] = useState(null); // {top, bottom, left}
  const [placement, setPlacement] = useState(null); // {top, left}
  const rootRef = useRef(null);
  const popRef = useRef(null);

  const toMinutes = (t) => {
    const p = parse(t);
    return p.h24 * 60 + p.m;
  };
  const emit = (next) => {
    let out = next;
    if (minTime && toMinutes(out) < toMinutes(minTime)) out = minTime;
    if (maxTime && toMinutes(out) > toMinutes(maxTime)) out = maxTime;
    onChange(out);
  };

  const { h24, m } = parse(value);
  const pm = h24 >= 12;
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;

  useEffect(() => {
    if (!open) return undefined;
    const inside = (el) =>
      (rootRef.current && rootRef.current.contains(el)) ||
      (popRef.current && popRef.current.contains(el));
    const onDown = (e) => {
      if (!inside(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    // A fixed popover would detach from its field if the rail scrolled or
    // the window resized underneath it: close instead of drifting.
    const onMove = (e) => {
      if (e.type === 'scroll' && popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open]);

  // Place once the popover has a size: below the field, flipping above
  // when the viewport bottom is close; clamped inside the viewport width.
  useLayoutEffect(() => {
    if (!open || !anchor || !popRef.current) return;
    const { offsetWidth: w, offsetHeight: h } = popRef.current;
    const gap = 4;
    const below = anchor.bottom + gap;
    const top =
      below + h > window.innerHeight - 8 ? anchor.top - gap - h : below;
    const left = Math.max(8, Math.min(anchor.left, window.innerWidth - w - 8));
    setPlacement({ top: Math.max(8, top), left });
  }, [open, anchor]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        title={title}
        onClick={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          setAnchor({ top: r.top, bottom: r.bottom, left: r.left });
          setPlacement(null);
          setOpen((v) => !v);
        }}
        className="flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded border border-slate-200 px-1.5 py-0.5 text-[11px] font-medium text-slate-700 hover:border-slate-300 disabled:opacity-50 dark:border-white/10 dark:text-slate-200 dark:hover:border-white/20"
      >
        <Clock size={10} className="opacity-50" />
        {formatTime(value)}
      </button>
      {open
        ? createPortal(
            <div
              ref={popRef}
              style={{
                position: 'fixed',
                top: placement?.top ?? -9999,
                left: placement?.left ?? -9999,
                visibility: placement ? 'visible' : 'hidden',
              }}
              className="z-[1080] flex rounded-lg bg-white shadow-[0_4px_20px_rgba(15,23,42,0.15)] ring-1 ring-slate-200/80 dark:!bg-surface-elevated dark:ring-white/10"
            >
              <Column
                items={HOURS}
                selected={h12}
                onPick={(v) => {
                  // Nearest-meridiem hour pick (Nic 2026-08-11): picking "11"
                  // while on 12 PM means 11 AM, not 11 PM — compose both
                  // candidates and keep the one closest to the current time.
                  // Tap AM/PM to override explicitly (a tie keeps the current
                  // meridiem).
                  const cur = h24 * 60 + m;
                  const cand = (isPm) => ((v % 12) + (isPm ? 12 : 0)) * 60 + m;
                  const dPm = Math.abs(cand(true) - cur);
                  const dAm = Math.abs(cand(false) - cur);
                  const usePm = dPm === dAm ? pm : dPm < dAm;
                  emit(compose(v, m, usePm));
                }}
              />
              <div className="my-2 w-px bg-slate-100 dark:bg-white/10" />
              <Column
                items={MINUTES}
                selected={m}
                onPick={(v) => emit(compose(h12, v, pm))}
                render={(v) => String(v).padStart(2, '0')}
              />
              <div className="my-2 w-px bg-slate-100 dark:bg-white/10" />
              <div className="flex flex-col justify-center gap-1 px-1.5">
                {['AM', 'PM'].map((mer) => (
                  <button
                    key={mer}
                    type="button"
                    onClick={() => emit(compose(h12, m, mer === 'PM'))}
                    className={`flex h-7 w-9 items-center justify-center rounded-md text-[11px] font-semibold transition-colors ${
                      (mer === 'PM') === pm
                        ? 'bg-slate-900 text-white dark:bg-slate-200 dark:!text-slate-900'
                        : 'text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-white/10'
                    }`}
                  >
                    {mer}
                  </button>
                ))}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
};

export default TimeField;
